import React, { useMemo, useState } from 'react';
import { Alert, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useQuery } from 'react-query';
import { teacherModuleAPI } from '../../services/api';
import MergeCandidateCards from './MergeCandidateCards';

function hhmm(value) {
  return value ? String(value).slice(0, 5) : '';
}

export default function MergeBatchPicker({ sessionId, value, onChange }) {
  const [nameInput, setNameInput] = useState('');
  const [submittedName, setSubmittedName] = useState('');

  const sameTimeQuery = useQuery(
    ['sessionMergeCandidates', sessionId],
    async () => {
      const res = await teacherModuleAPI.getSessionMergeCandidates(sessionId);
      return {
        candidates: res.data?.data || [],
        source: res.data?.source || null,
      };
    },
    { enabled: !!sessionId }
  );

  const nameQuery = useQuery(
    ['sessionMergeByName', sessionId, submittedName],
    async () => {
      const res = await teacherModuleAPI.getSessionMergeCandidates(sessionId, { batchName: submittedName });
      return {
        candidates: res.data?.data || [],
        source: res.data?.source || null,
      };
    },
    { enabled: !!sessionId && submittedName.length >= 2 }
  );

  const sameTime = useMemo(
    () => sameTimeQuery.data?.candidates || [],
    [sameTimeQuery.data?.candidates]
  );
  const named = useMemo(
    () => nameQuery.data?.candidates || [],
    [nameQuery.data?.candidates]
  );
  const source = nameQuery.data?.source || sameTimeQuery.data?.source || null;
  const selected = useMemo(
    () => [...sameTime, ...named].find((c) => c.sessionId === value) || null,
    [named, sameTime, value]
  );

  const runSearch = () => {
    const next = nameInput.trim();
    setSubmittedName(next);
    if (next.length < 2) onChange('');
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2">
        Pick a class at the same time, or type another batch name even if that class is at a different time.
      </Typography>

      {sameTimeQuery.isLoading ? (
        <CircularProgress size={22} />
      ) : sameTime.length === 0 ? (
        <Alert severity="info" sx={{ py: 0 }}>
          No batches at the same time with spare seats. Type a batch name below to merge into a class at a
          different time.
        </Alert>
      ) : (
        <MergeCandidateCards candidates={sameTime} value={value || ''} onChange={onChange} />
      )}

      <Typography variant="subtitle2" sx={{ pt: 0.5 }}>
        Find another batch by name
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          fullWidth
          label="Batch name"
          placeholder="e.g. USA 201"
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              runSearch();
            }
          }}
        />
        <Button variant="outlined" onClick={runSearch} disabled={nameInput.trim().length < 2}>
          Find
        </Button>
      </Stack>

      {submittedName.length >= 2 ? (
        nameQuery.isLoading ? (
          <CircularProgress size={22} />
        ) : named.length === 0 ? (
          <Alert severity="warning" sx={{ py: 0 }}>
            No class found for “{submittedName}” on {source?.sessionDateLabel || source?.sessionDate || 'this date'}.
          </Alert>
        ) : (
          <MergeCandidateCards candidates={named} value={value || ''} onChange={onChange} />
        )
      ) : null}

      {selected?.timeDiffers ? (
        <Alert severity="warning">
          Confirm: this class will move to {selected.sessionDateLabel || selected.sessionDate}{' '}
          {hhmm(selected.startTime)}–{hhmm(selected.endTime)} to match {selected.batchName}
          {source?.startTime
            ? ` (currently ${hhmm(source.startTime)}–${hhmm(source.endTime)})`
            : ''}
          . Students join that host class.
        </Alert>
      ) : selected ? (
        <Alert severity="info" sx={{ py: 0 }}>
          Students will join {selected.batchName} on {selected.sessionDateLabel || selected.sessionDate}{' '}
          {hhmm(selected.startTime)}–{hhmm(selected.endTime)}.
        </Alert>
      ) : null}
    </Stack>
  );
}
